import {
  getEffectiveGoogleAccessToken,
  looksLikeGoogleAccessToken,
} from '../lib/googleAccessToken';

import { GOOGLE_ERROR_MESSAGES } from '../lib/googleErrorMessages';
import {
  classifyError,
  type ReliabilityError,
  type ErrorContext,
} from '../lib/reliability/errorModel';

import { auth } from '../lib/firebase';

import type {
  ClassroomRubric,
  ClassroomRubricCriterion,
  ClassroomTopic,
  ExportResults,
  ExportSessionResponse,
  ReviewItem,
  ScanAssignment,
  ScanCourse,
  ScanMatchReason,
  ScanPage,
  ScanRosterStudent,
  ScanRubric,
  ScanSession,
  ScanSubmission,
} from '../lib/scanGrading/types';
import type { GradeProgress } from '../lib/scanGrading/gradeProgress';
import { normalizeFeedbackTone, type FeedbackTone } from '../lib/feedback/feedbackTone';
import { isLocalCourseId } from '../lib/scanGrading/courseIds';

function apiBase(): string {
  const env = import.meta.env.VITE_BILLING_API_BASE as string | undefined;

  if (env?.trim()) return env.replace(/\/$/, '');

  if (typeof window !== 'undefined') return window.location.origin;

  return '';
}

/**
 * Generate a stable, opaque idempotency key for a single
 * client-initiated create attempt.
 *
 * The key is sent on `POST /api/classroom/courses` and
 * `POST /api/classroom/courses/:courseId/assignments` so the server
 * can dedupe replays of the same logical create — preventing a
 * duplicate Google Classroom course / coursework AND a duplicate
 * DG row when a teacher taps "Create" twice, when an offline replay
 * queue retries the request, or when a network flake triggers a
 * silent retry.
 *
 * Prefers `crypto.randomUUID()` (Web Crypto / Node ≥14.17). Falls
 * back to a Math.random-based shape for the rare runtimes where
 * `crypto.randomUUID` is unavailable; the format is irrelevant to
 * the server (it treats the key as opaque), only uniqueness matters.
 */
export function makeIdempotencyKey(): string {
  try {
    if (typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID) {
      return globalThis.crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  // Defensive fallback. Format matches the UUID v4 shape so log
  // scanners that filter by shape still treat the value the same.
  const rand = () => Math.random().toString(16).slice(2).padEnd(12, '0');
  return `${rand().slice(0, 8)}-${rand().slice(0, 4)}-4${rand().slice(0, 3)}-${rand().slice(0, 4)}-${rand().slice(0, 12)}`;
}

export type ScanApiErrorBody = {
  error?: string;
  code?: string;
  message?: string;
  model?: string;
  providerStatus?: string;
  providerMessage?: string;
};

export type GoogleLinkStatus = {
  linkedGoogle: boolean;
  email?: string;
  connectedAt?: string;
  /** Raw scope strings exactly as Google returned them. */
  scopes?: string[];
  /** Same scopes, normalized to the short-id form (`classroom.profile.emails`). */
  normalizedScopes?: string[];
  /**
   * True when the server has a non-empty granted scope list to compare
   * against. Legacy linked accounts without a stored scope list report
   * `false`, which lets the UI fall back to a capability probe rather
   * than confidently claiming a scope is missing.
   */
  grantedScopesKnown?: boolean;
  liveScopeVerificationAvailable?: boolean;
  reconnectRequired?: boolean;
  /** Short-id form of any required scopes that aren't covered. */
  missingScopes?: string[];
  /** Back-compat alias — same content as `missingScopes` but full URLs. */
  missingRequiredScopes?: string[];
  drivePermissionMissing?: boolean;
  hasDriveFileScope?: boolean | null;
  /**
   * True when the linked Google account is missing the
   * `classroom.profile.emails` scope. The roster API silently omits
   * `profile.emailAddress` in that case, so the publish UI must show a
   * "Reconnect Google to email students" prompt before defaulting the
   * email toggle on.
   */
  studentEmailScopeMissing?: boolean;
  /**
   * Explicit booleans. `null` means "we can't tell from the stored
   * grant — capability probe required" (legacy accounts without a
   * persisted scope list). The Classroom coursework READ scopes
   * (`classroom.coursework.students.readonly` and
   * `classroom.coursework.me.readonly`) are tracked SEPARATELY from
   * the WRITE scopes — Google treats `students` and `students.readonly`
   * as distinct, so callers MUST NOT assume one implies the other.
   */
  hasClassroomProfileEmailsScope?: boolean | null;
  hasGmailSendScope?: boolean | null;
  hasClassroomCoursesReadonlyScope?: boolean | null;
  /**
   * Write-scope counterpart for `classroom.courses.readonly`. Required
   * by Google's `courses.create` endpoint — Google does NOT treat the
   * readonly variant as satisfying course-create permission. `null`
   * means the stored grant has no scope list at all (legacy linked
   * account); the UI must treat that as "unknown" and probe the create
   * result rather than confidently reporting "missing".
   */
  hasClassroomCoursesScope?: boolean | null;
  hasClassroomRostersReadonlyScope?: boolean | null;
  hasClassroomCourseworkStudentsScope?: boolean | null;
  hasClassroomCourseworkStudentsReadonlyScope?: boolean | null;
  hasClassroomCourseworkMeScope?: boolean | null;
  hasClassroomCourseworkMeReadonlyScope?: boolean | null;
  /**
   * Phase B (Topics): `classroom.topics` covers both
   * `courses.topics.list` (read) and `courses.topics.create` (write).
   * `null` means the stored grant has no scope list at all (legacy
   * linked account); the UI must treat that as "unknown" and rely on
   * the actual API result instead of confidently reporting "missing".
   */
  hasClassroomTopicsScope?: boolean | null;
  knownScopeIds?: {
    classroomProfileEmails?: string;
    driveFile?: string;
    gmailSend?: string;
    classroomCoursesReadonly?: string;
    classroomRostersReadonly?: string;
    classroomCourseworkStudents?: string;
    classroomCourseworkStudentsReadonly?: string;
    classroomCourseworkMe?: string;
    classroomCourseworkMeReadonly?: string;
  };
};

export class ScanApiError extends Error {
  status: number;

  body: ScanApiErrorBody;

  constructor(message: string, status: number, body: ScanApiErrorBody) {
    super(message);
    this.name = 'ScanApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Universal structured description of any API/network error thrown by this
 * module. Components use this to decide consistently whether to show a retry
 * button, a reconnect prompt, an "already deleted" notice, or a validation
 * hint — for ALL teacher actions, not just Classroom sync. The friendly
 * `ScanApiError.message` is preserved as the default `userMessage`.
 */
export function describeApiError(err: unknown, context?: ErrorContext): ReliabilityError {
  return classifyError(err, context);
}

/** @deprecated Use getEffectiveGoogleAccessToken from lib/googleAccessToken */

export function resolveGisAccessTokenForApi(googleAccessToken?: string | null): string | null {
  return getEffectiveGoogleAccessToken(googleAccessToken);
}

export { getEffectiveGoogleAccessToken, looksLikeGoogleAccessToken };

function isGoogleOrClassroomPath(path: string): boolean {
  return path.startsWith('/api/google') || path.startsWith('/api/classroom');
}

async function resolveFirebaseIdToken(forceRefresh = false): Promise<string | null> {
  const user = auth.currentUser;

  if (!user) return null;

  try {
    return await user.getIdToken(forceRefresh);
  } catch {
    return null;
  }
}

/**

 * Bearer for /api/google/* and /api/classroom/* — always Firebase app auth.
 * The backend owns all Google API access-token lookup and refresh.

 */

export async function resolveGoogleClassroomApiBearer(
  _googleAccessToken?: string | null
): Promise<string | null> {
  return resolveFirebaseIdToken();
}

/** Bearer the scan API accepts: GIS when available, else Firebase ID token (non-Google routes). */

export async function resolveScanApiBearer(
  googleAccessToken?: string | null
): Promise<string | null> {
  const gis = getEffectiveGoogleAccessToken(googleAccessToken);

  if (gis) return gis;

  return resolveFirebaseIdToken();
}

/** True when the DoneGrading API responds (does not require auth). */

export async function checkApiReachable(): Promise<boolean> {
  const url = `${apiBase()}/api/health`;
  try {
    const res = await fetch(url, { method: 'GET' });
    return res.ok;
  } catch {
    return false;
  }
}

function devLogGoogleApi(message: string, data: Record<string, unknown>) {
  if (!import.meta.env.DEV) return;

  console.info(`[DoneGrading] ${message}`, data);
}

const TYPED_ERROR_MESSAGES: Record<string, string> = {
  // New stable Google codes (Phase 2). GOOGLE_RECONNECT_REQUIRED is
  // intentionally NOT spread here so the established reconnect copy below is
  // preserved for the assignment/rubric API paths.
  GOOGLE_SCOPE_MISSING: GOOGLE_ERROR_MESSAGES.GOOGLE_SCOPE_MISSING,
  GOOGLE_RATE_LIMITED: GOOGLE_ERROR_MESSAGES.GOOGLE_RATE_LIMITED,
  GOOGLE_TEMPORARY_FAILURE: GOOGLE_ERROR_MESSAGES.GOOGLE_TEMPORARY_FAILURE,
  GOOGLE_PERMISSION_DENIED: GOOGLE_ERROR_MESSAGES.GOOGLE_PERMISSION_DENIED,
  GOOGLE_TIMEOUT: GOOGLE_ERROR_MESSAGES.GOOGLE_TIMEOUT,
  GOOGLE_UNKNOWN_ERROR: GOOGLE_ERROR_MESSAGES.GOOGLE_UNKNOWN_ERROR,
  AUTH_RECONNECT_REQUIRED: 'Google connection expired. Reconnect Google.',
  AUTH_PERMISSION_REQUIRED:
    'Google permission needed to load Classroom assignments. Reconnect Google to grant the required Classroom permissions.',
  COURSE_ID_MAPPING_ERROR:
    'This course needs to be refreshed from Classroom. Sync from Classroom and try again.',
  CLASSROOM_COURSE_NOT_FOUND:
    'This Classroom course could not be found. Sync from Classroom and try again.',
  CLASSROOM_COURSE_ID_STALE:
    'This Classroom course id is out of date. Sync from Classroom to refresh and try again.',
  CLASSROOM_TOPIC_NOT_FOUND:
    'This Classroom topic could not be found. Sync from Classroom and try again.',
  CLASSROOM_ROSTER_NOT_FOUND:
    'This Classroom roster could not be found. Sync from Classroom and try again.',
  CLASSROOM_RUBRIC_NOT_FOUND:
    'This Classroom rubric could not be found. Sync from Classroom and try again.',
  CLASSROOM_COURSE_RECONCILIATION_AMBIGUOUS:
    'More than one Classroom course matches this DoneGrading course. Open Classroom and pick the correct course, then sync from Classroom and try again.',
  CLASSROOM_COURSEWORK_ACCESS_DENIED:
    'Classroom assignments are unavailable for this course. You can still create a DoneGrading assignment and grade papers locally.',
  CLASSROOM_COURSEWORK_LIST_DENIED:
    'Classroom assignments are unavailable for this course. You can still create a DoneGrading assignment and grade papers locally.',
  CLASSROOM_COURSEWORK_CREATE_DENIED:
    'Google Classroom denied assignment creation for this course. You may not have permission to create coursework through the API for this class.',
  ASSIGNMENTS_LOAD_FAILED: 'Could not load assignments for this course.',
  ROSTER_LOAD_FAILED: 'Could not load roster for this course.',
  COURSE_SYNC_FAILED: 'Could not sync courses from Classroom.',
  COURSE_CREATE_FAILED: 'Could not create course.',
  COURSE_PATCH_FAILED: 'Could not update this Classroom course.',
  COURSE_DELETE_FAILED: 'Could not delete this Classroom course.',
  COURSE_DELETE_REQUIRES_ARCHIVE:
    'Archive this course in Google Classroom before deleting it. Google only allows deleting archived courses.',
  ASSIGNMENT_PATCH_FAILED: 'Could not update this Classroom assignment.',
  ASSIGNMENT_DELETE_FAILED: 'Could not delete this Classroom assignment.',
  ASSIGNMENT_PATCH_NO_FIELDS: 'No assignment fields were provided to update.',
  COURSEWORK_PATCH_INVALID_FIELD:
    'One of the fields you tried to update is not editable on a Classroom assignment.',
  INVALID_STATE:
    'Assignment state must be either Draft or Published. To delete an assignment, use the delete action.',
  INVALID_TITLE: 'Assignment title cannot be empty.',
  INVALID_DUE_DATE:
    'Pick a due date before setting a due time, or clear both to remove the due date.',
  INVALID_MAX_POINTS: 'Max points must be a whole number greater than or equal to zero.',
  CLASSROOM_ASSIGNMENT_NOT_FOUND:
    'This Classroom assignment could not be found. Sync from Classroom and try again.',
  TOPICS_LIST_FAILED: 'Could not load topics for this Classroom course.',
  TOPIC_CREATE_FAILED: 'Could not create the Classroom topic.',
  TOPIC_NAME_REQUIRED: 'Topic name cannot be empty.',
  TOPIC_NOT_FOUND:
    'That topic no longer exists in Google Classroom. Pick a different topic or refresh the list.',
  RUBRIC_LIST_FAILED: 'Could not load the rubric for this Classroom assignment.',
  RUBRIC_CREATE_FAILED: 'Could not create the rubric on this Classroom assignment.',
  RUBRIC_UPDATE_FAILED: 'Could not update the rubric on this Classroom assignment.',
  RUBRIC_DELETE_FAILED: 'Could not delete the rubric on this Classroom assignment.',
  RUBRIC_ALREADY_EXISTS:
    'This Classroom assignment already has a rubric. Edit the existing rubric instead.',
  RUBRIC_NOT_FOUND: 'This Classroom assignment does not have a rubric to update or delete.',
  RUBRIC_NO_CRITERIA: 'A rubric must have at least one criterion.',
  RUBRIC_CRITERION_TITLE_REQUIRED: 'Each rubric criterion needs a title.',
  RUBRIC_LEVELS_REQUIRED: 'Each rubric criterion needs at least one level.',
  RUBRIC_LEVEL_TITLE_REQUIRED: 'Each rubric level needs a title.',
  RUBRIC_LEVEL_INVALID_POINTS: 'Rubric level points must be a non-negative number.',
  RUBRIC_WRITE_NOT_AVAILABLE:
    'Rubrics are not available for this assignment. This usually requires a Google Workspace for Education account, or an assignment created by DoneGrading.',
  ASSIGNMENT_NOT_SYNCED:
    'This assignment is not connected to Google Classroom, so it cannot have a rubric.',
  // Scan-session creation: a referenced class/assignment/rubric is stale or
  // missing. Surface a specific next action instead of a bare "Not found".
  SCAN_COURSE_NOT_FOUND: 'This class is no longer available. Refresh your classes and try again.',
  SCAN_ASSIGNMENT_NOT_FOUND:
    'This assignment is no longer available. Refresh assignments and try again.',
  SCAN_RUBRIC_NOT_FOUND: 'This rubric is no longer available. Please choose another rubric.',
};

function friendlyApiError(
  status: number,

  body: ScanApiErrorBody,

  opts?: { hasGoogleAccessToken?: boolean; hasFirebaseAuth?: boolean }
): string {
  const raw = body.error?.trim();

  const signedInWithGoogle = !!opts?.hasGoogleAccessToken;

  const hasFirebase = !!opts?.hasFirebaseAuth;

  if (status === 413) {
    return (
      body.message?.trim() ||
      'The scanned page image is too large. Try moving closer or retaking the photo.'
    );
  }

  if (body.error === 'ai_model_unavailable') {
    return body.message?.trim() || 'The configured AI model is unavailable.';
  }

  if (body.code && TYPED_ERROR_MESSAGES[body.code]) {
    return TYPED_ERROR_MESSAGES[body.code];
  }

  if (body.code === 'GOOGLE_NOT_LINKED') {
    return 'Reconnect Google Classroom to continue.';
  }

  if (
    body.code === 'AUTH_RECONNECT_REQUIRED' ||
    body.code === 'GOOGLE_RECONNECT_REQUIRED' ||
    body.code === 'GOOGLE_REFRESH_FAILED' ||
    body.code === 'GOOGLE_TOKEN_INVALID'
  ) {
    return 'Google connection expired. Reconnect Google.';
  }

  if (status === 401) {
    if (raw && raw !== 'Unauthorized' && raw !== 'Authorization required') {
      return raw;
    }

    if (hasFirebase && body.code === 'AUTH_REQUIRED') {
      return 'Sign in to DoneGrading before connecting Google Classroom.';
    }

    if (signedInWithGoogle && body.code === 'AUTH_REQUIRED') {
      return 'Google sign-in succeeded, but no session token was sent to the server. Tap Continue to retry linking.';
    }

    if (signedInWithGoogle && body.code === 'AUTH_INVALID') {
      return raw?.includes('expired')
        ? raw
        : 'Your Google session expired or was rejected. Sign in with Google again using your school Classroom account.';
    }

    if (signedInWithGoogle) {
      return 'Google sign-in succeeded, but Classroom linking failed. Check that you used your school account, then tap Continue to retry.';
    }

    if (body.code === 'AUTH_INVALID') {
      return 'We could not verify your sign-in. Sign in with Google again using your school account.';
    }

    return 'Sign in with Google to connect Classroom. Use the same school account you use in Google Classroom.';
  }

  if (status === 403) {
    return 'You do not have permission for this action. Check your Google Classroom access.';
  }

  if (raw && raw !== 'Unauthorized') {
    return raw;
  }

  return `Request failed (${status})`;
}

const SIGN_IN_AGAIN =
  'Sign in with Google again to connect Classroom. Use the same school account you use in Google Classroom.';

const CONNECT_CLASSROOM =
  'Sign in to DoneGrading, then connect Google Classroom with your school account.';

async function authHeaders(
  path: string,

  googleAccessToken?: string | null
): Promise<HeadersInit> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  const googleRoute = isGoogleOrClassroomPath(path);

  const bearer = googleRoute
    ? await resolveGoogleClassroomApiBearer(googleAccessToken)
    : await resolveScanApiBearer(googleAccessToken);

  const bearerKind = !bearer ? 'none' : looksLikeGoogleAccessToken(bearer) ? 'gis' : 'firebase';

  if (bearer) {
    headers.Authorization = `Bearer ${bearer}`;
  }

  if (googleRoute && import.meta.env.DEV) {
    devLogGoogleApi('google/classroom API auth', {
      path,

      bearerKind,

      bearerLength: bearer?.length ?? 0,

      firebaseUser: !!auth.currentUser,
    });
  }

  return headers;
}

async function apiFetch<T>(
  path: string,

  init?: RequestInit & { googleAccessToken?: string | null }
): Promise<T> {
  const { googleAccessToken, ...rest } = init || {};

  const effectiveGis = getEffectiveGoogleAccessToken(googleAccessToken);

  const googleRoute = isGoogleOrClassroomPath(path);

  const hasFirebase = !!auth.currentUser;

  if (googleRoute && !hasFirebase) {
    throw new Error(CONNECT_CLASSROOM);
  }

  const builtHeaders = await authHeaders(path, googleAccessToken);

  const authHeader = (builtHeaders as Record<string, string>).Authorization;

  if (googleRoute && !authHeader?.trim()) {
    throw new Error(hasFirebase ? CONNECT_CLASSROOM : SIGN_IN_AGAIN);
  }

  let res: Response;

  try {
    const url = `${apiBase()}${path}`;
    const callerHeaders = (rest.headers || {}) as Record<string, string>;
    // Caller Content-Type must win for binary uploads (otherwise JSON
    // default from authHeaders corrupts the raw body contract).
    res = await fetch(url, {
      ...rest,

      headers: {
        ...builtHeaders,
        ...callerHeaders,
      },
    });
  } catch (e) {
    throw wrapNetworkError(e, effectiveGis);
  }

  const rawText =
    typeof res.text === 'function'
      ? await res.text()
      : JSON.stringify(await res.json().catch(() => ({})));

  const parsedBody = parseApiJsonBody(rawText, res, path);

  if (!res.ok) {
    if (parsedBody === null) {
      throw new Error(nonJsonApiResponseMessage(path, res, rawText));
    }

    const body = (parsedBody ?? {}) as ScanApiErrorBody;

    if (googleRoute && import.meta.env.DEV) {
      devLogGoogleApi('google/classroom API error', {
        path,
        status: res.status,
        frontendErrorCodeReceived: body.code,
        message: body.message || body.error,
      });
    }

    throw new ScanApiError(
      friendlyApiError(res.status, body, {
        hasGoogleAccessToken: !!effectiveGis,

        hasFirebaseAuth: hasFirebase,
      }),
      res.status,
      body
    );
  }

  if (parsedBody === null) {
    throw new Error(nonJsonApiResponseMessage(path, res, rawText));
  }

  return parsedBody as T;
}

function parseApiJsonBody(rawText: string, res: Response, path: string): unknown | null {
  const contentType = res.headers?.get?.('content-type') || '';
  const trimmed = rawText.trim();
  const looksLikeHtml = trimmed.startsWith('<');
  const looksLikeJson = trimmed.startsWith('{') || trimmed.startsWith('[');
  const declaresJson = contentType.toLowerCase().includes('application/json');

  if (!declaresJson && (looksLikeHtml || !looksLikeJson)) {
    return null;
  }

  if (!trimmed) {
    return null;
  }

  try {
    return JSON.parse(rawText);
  } catch {
    throw new Error(nonJsonApiResponseMessage(path, res, rawText));
  }
}

function nonJsonApiResponseMessage(path: string, res: Response, rawText: string): string {
  const returnedHtml = rawText.trimStart().startsWith('<');
  const responseKind = returnedHtml ? 'HTML' : 'non-JSON';

  if (/\/api\/classroom\/courses\/[^/]+\/topics(?:$|\?)/.test(path)) {
    return `Failed to load topics (server returned ${responseKind}; status ${res.status})`;
  }

  if (/\/api\/classroom\/courses\/[^/]+\/assignments\/[^/]+\/rubric(?:$|\?)/.test(path)) {
    return `Failed to load rubric (server returned ${responseKind}; status ${res.status})`;
  }

  return `API request failed (server returned ${responseKind}; status ${res.status})`;
}

function wrapNetworkError(e: unknown, googleAccessToken?: string | null): Error {
  if (e instanceof TypeError) {
    const signedIn = !!googleAccessToken;

    return new Error(
      signedIn
        ? 'Cannot reach the DoneGrading server. Start the API (npm run dev in the project root) or check your network, then tap Continue to retry.'
        : 'Cannot reach the DoneGrading server. Check your network or start the API (npm run dev), then sign in again.'
    );
  }

  return e instanceof Error ? e : new Error(String(e));
}

export type GoogleLinkBody =
  | { authorizationCode: string }
  | { code: string }
  | { accessToken: string; refreshToken?: string };

function normalizeLinkBody(payload: GoogleLinkBody): Record<string, string> {
  if ('authorizationCode' in payload && payload.authorizationCode) {
    return { code: payload.authorizationCode.trim() };
  }

  if ('code' in payload && payload.code) {
    return { code: payload.code.trim() };
  }

  if ('accessToken' in payload) {
    return {
      accessToken: payload.accessToken.trim(),

      ...(payload.refreshToken ? { refreshToken: payload.refreshToken } : {}),
    };
  }

  throw new Error('Invalid Google link payload');
}

export async function getGoogleLinkStatus() {
  const res = await apiFetch<GoogleLinkStatus>('/api/google/status', {
    googleAccessToken: null,
  });

  if (import.meta.env.DEV) {
    devLogGoogleApi('GET /api/google/status', { linkedGoogle: res.linkedGoogle });
  }

  return res;
}

/** Link Classroom OAuth to the authenticated teacher (Firebase Bearer + authorization code). */

export async function linkGoogleAccount(payload: GoogleLinkBody): Promise<{
  linkedGoogle: boolean;

  email?: string;
}> {
  if (!auth.currentUser) {
    throw new Error(CONNECT_CLASSROOM);
  }

  const body = normalizeLinkBody(payload);

  if ('accessToken' in body) {
    throw new Error(CONNECT_CLASSROOM);
  }

  const idToken = await resolveFirebaseIdToken(true);

  if (!idToken) {
    throw new Error(CONNECT_CLASSROOM);
  }

  if (import.meta.env.DEV) {
    devLogGoogleApi('POST /api/google/link', {
      firebaseUser: !!auth.currentUser,

      idTokenLength: idToken.length,

      startingOAuth: false,

      callingGoogleLink: true,

      hasCode: 'code' in body,
    });
  }

  const result = await apiFetch<{ linkedGoogle: boolean; email?: string }>('/api/google/link', {
    method: 'POST',

    body: JSON.stringify(body),

    googleAccessToken: null,
  });

  if (import.meta.env.DEV) {
    devLogGoogleApi('POST /api/google/link response', {
      status: 'ok',

      linkedGoogle: result.linkedGoogle,
    });
  }

  return result;
}

export interface SyncClassroomResult {
  ok: boolean;
  imported: number;
  /** True when courses were served from fresh local cache (no Google call). */
  fromCache?: boolean;
  /** True when at least the course list was refreshed from Google. */
  refreshed?: boolean;
  stages?: {
    courses?: 'cached' | 'refreshed';
    coursework?: 'cached' | 'refreshed';
    roster?: 'cached' | 'refreshed';
  };
  warning?: { code: string; message: string };
}

/**
 * Sync Google Classroom courses (+ deep-sync assignments/rosters).
 *
 * `force` bypasses the server freshness windows (courses 15m, coursework 10m,
 * roster 5m). The manual "Sync" button passes `force: true`; the one-time
 * automatic post-link sync leaves it `false` so a recently-synced teacher is
 * not re-hitting Google on every visit.
 */
export async function syncClassroomCourses(
  googleAccessToken?: string | null,
  options?: { force?: boolean }
) {
  return apiFetch<SyncClassroomResult>('/api/classroom/sync', {
    method: 'POST',
    body: JSON.stringify({ force: options?.force === true }),
    googleAccessToken,
  });
}

export interface ClassroomSyncStatus {
  ok: boolean;
  linked: boolean;
  status: 'idle' | 'syncing' | 'not_linked' | 'reconnect_required';
  /**
   * True when the user's Google grant can no longer be refreshed and
   * automatic background sync is suppressed until they reconnect.
   */
  reconnectRequired?: boolean;
  autoSyncEnabled: boolean;
  pushEnabled: boolean;
  pushAvailable: boolean;
  devFallbackPolling: boolean;
  safetyIntervalMinutes: number;
  /** Most recent of manual / auto / courses sync timestamps (ISO) or null. */
  lastSyncedAt: string | null;
  lastManualSyncedAt: string | null;
  lastAutoSyncedAt: string | null;
  queue: { pending: number; running: number; total: number };
}

/**
 * Lightweight auto-sync status. The frontend polls this every 30–60s
 * while open and refreshes course/assignment lists when `lastSyncedAt`
 * advances (i.e. a background auto-sync changed local state).
 */
export async function getClassroomSyncStatus(googleAccessToken?: string | null) {
  return apiFetch<ClassroomSyncStatus>('/api/classroom/sync/status', { googleAccessToken });
}

export async function listScanCourses(
  googleAccessToken?: string | null,
  opts?: { bypassCache?: boolean }
) {
  const q = opts?.bypassCache ? '?refresh=1' : '';
  return apiFetch<{ courses: ScanCourse[] }>(`/api/classroom/courses${q}`, { googleAccessToken });
}

/**
 * Persist a teacher-curated linear ordering for the caller's courses.
 * The server treats the body as an ordered list of LOCAL DG course
 * ids and assigns `sortIndex = position` to each. Ids missing from
 * the array keep their existing `sortIndex` (or stay undefined for
 * legacy rows that never had one — the list endpoint sorts those to
 * the end). Reordering is per-user across all tabs; the Published /
 * Archived tabs render filtered subsets of this same sequence.
 */
export async function setCourseOrder(
  orderedCourseIds: string[],
  googleAccessToken?: string | null
) {
  return apiFetch<{ courses: ScanCourse[] }>('/api/classroom/courses/order', {
    method: 'PUT',
    body: JSON.stringify({ orderedCourseIds }),
    googleAccessToken,
  });
}

/**
 * Persist a teacher-curated linear ordering for a single course's
 * assignments. Mirrors `setCourseOrder` but scoped to one course; the
 * server only mutates assignments whose `courseId` matches the
 * resolved local course.
 */
export async function setAssignmentOrder(
  courseId: string,
  orderedAssignmentIds: string[],
  googleAccessToken?: string | null
) {
  return apiFetch<{ assignments: ScanAssignment[] }>(
    `/api/classroom/courses/${courseId}/assignments/order`,
    {
      method: 'PUT',
      body: JSON.stringify({ orderedAssignmentIds }),
      googleAccessToken,
    }
  );
}

/**
 * Optional knobs for `createScanCourse`. When `createInClassroom` is
 * true (or `source: 'classroom'`), the server attempts a real
 * `courses.create` call against Google Classroom and persists the
 * returned `classroomCourseId`. Omitting both keeps the legacy
 * DoneGrading-only local create behavior — preserves English 101 test
 * paths that POST `{ name, period }` without flags.
 *
 * `localOnly: true` is the explicit "create DoneGrading-only course"
 * marker. The server treats it the same as `source: 'donegrading'`
 * (no Google call); we accept both so call sites can be
 * self-documenting (handleCreateLocalCourse always sets both).
 */
export type CreateScanCourseOptions = {
  section?: string;
  source?: 'classroom' | 'donegrading';
  createInClassroom?: boolean;
  localOnly?: boolean;
  /**
   * Optional client-supplied idempotency key. The service layer
   * generates one automatically per call (`makeIdempotencyKey`),
   * but callers MAY pass a stable key explicitly when the same
   * logical create must survive retries (e.g. an offline replay
   * queue that persists the key alongside the request body, or a
   * button handler that wants the second tap to dedupe rather
   * than create twice).
   */
  idempotencyKey?: string;
};

export async function createScanCourse(
  name: string,

  period: string,

  googleAccessToken?: string | null,
  options?: CreateScanCourseOptions
) {
  const body: Record<string, unknown> = { name, period };
  if (options?.section) body.section = options.section;
  if (options?.source) body.source = options.source;
  if (options?.createInClassroom) body.createInClassroom = true;
  if (options?.localOnly) body.localOnly = true;
  const idempotencyKey =
    typeof options?.idempotencyKey === 'string' && options.idempotencyKey.length > 0
      ? options.idempotencyKey
      : makeIdempotencyKey();
  // Send the key on BOTH the header and the body for back-compat
  // with proxies that strip non-standard headers; the server reads
  // header first then body field. Sending on both is harmless and
  // costs only a few bytes per request.
  body.idempotencyKey = idempotencyKey;
  return apiFetch<{ course: ScanCourse }>('/api/classroom/courses', {
    method: 'POST',

    body: JSON.stringify(body),

    headers: { 'Idempotency-Key': idempotencyKey },

    googleAccessToken,
  });
}

/**
 * Patch the Google Classroom-managed fields on a synced course. The
 * server enforces an allow-list of writable keys (`name`, `section`,
 * `room`, `descriptionHeading`, `description`, `courseState`); anything
 * else is rejected at the route layer. `courseState` only accepts the
 * two values the v2 UI uses today — `ACTIVE` or `ARCHIVED`. The other
 * Classroom states (`PROVISIONED`, `DECLINED`, `SUSPENDED`) are not
 * reachable from this client because the v2 UI never asks for them.
 */
export type ClassroomCoursePatch = {
  name?: string;
  section?: string;
  room?: string;
  descriptionHeading?: string;
  description?: string;
  courseState?: 'ACTIVE' | 'ARCHIVED';
};

export async function editClassroomCourse(
  courseId: string,
  patch: ClassroomCoursePatch,
  googleAccessToken?: string | null
) {
  return apiFetch<{ course: ScanCourse }>(`/api/classroom/courses/${courseId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
    googleAccessToken,
  });
}

/**
 * Move a Google Classroom course to ARCHIVED. Thin wrapper around the
 * shared PATCH route so the v2 kebab menu can call a verb-named API
 * without restating the patch shape inline.
 */
export async function archiveClassroomCourse(courseId: string, googleAccessToken?: string | null) {
  return editClassroomCourse(courseId, { courseState: 'ARCHIVED' }, googleAccessToken);
}

/** Move an archived Google Classroom course back to ACTIVE. */
export async function restoreClassroomCourse(courseId: string, googleAccessToken?: string | null) {
  return editClassroomCourse(courseId, { courseState: 'ACTIVE' }, googleAccessToken);
}

/**
 * Delete a Google Classroom course. Will surface
 * `COURSE_DELETE_REQUIRES_ARCHIVE` (HTTP 409) on the `ScanApiError`
 * `body.code` when the server-side `courseState` is anything other
 * than `ARCHIVED` — the caller is expected to translate that into a
 * "Archive first" CTA rather than a raw error.
 */
export async function deleteClassroomCourse(courseId: string, googleAccessToken?: string | null) {
  return apiFetch<{ ok: boolean }>(`/api/classroom/courses/${courseId}`, {
    method: 'DELETE',
    googleAccessToken,
  });
}

/**
 * Phase A: PATCH a single Classroom-synced assignment. The server
 * enforces the allow-list (title, description, maxPoints, dueDate,
 * dueTime, state) and rejects anything else with the typed
 * `COURSEWORK_PATCH_INVALID_FIELD` code. `state` is restricted to
 * `'DRAFT'` / `'PUBLISHED'` — clients MUST use `deleteClassroomAssignment`
 * to delete, not a `state: 'DELETED'` patch.
 *
 * Special-case "clear the due date": pass `{ dueDate: null,
 * dueTime: null }`. The server translates that into Google's
 * "clear a field" idiom (the fields are listed in the updateMask but
 * omitted from the body).
 */
export type ClassroomAssignmentPatch = {
  title?: string;
  description?: string;
  maxPoints?: number;
  state?: 'DRAFT' | 'PUBLISHED';
  dueDate?: { year: number; month: number; day: number } | null;
  dueTime?: { hours?: number; minutes?: number; seconds?: number; nanos?: number } | null;
  /**
   * Phase B (Topics): set, change, or clear the assignment's
   * Classroom topic.
   *   - `'<topicId>'` → assign / change to that topic
   *   - `null`        → clear the topic (Google "(No topic)" bucket)
   *   - omitted       → no change to the topic
   * The server validates the supplied id against the cached topic
   * list and refreshes once on miss before rejecting with
   * `TOPIC_NOT_FOUND`.
   */
  topicId?: string | null;
};

export async function editClassroomAssignment(
  courseId: string,
  assignmentId: string,
  patch: ClassroomAssignmentPatch,
  googleAccessToken?: string | null
) {
  return apiFetch<{ assignment: ScanAssignment; updateMask?: string }>(
    `/api/classroom/courses/${courseId}/assignments/${assignmentId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(patch),
      googleAccessToken,
    }
  );
}

/**
 * Phase A: DELETE a Classroom-synced assignment. The server calls
 * Google's `DELETE /v1/courses/{courseId}/courseWork/{id}` (the
 * canonical delete operation per Google's docs — Classroom's own UI
 * uses this), then drops the persisted DG row so the assignment
 * list refresh removes the row.
 */
export async function deleteClassroomAssignment(
  courseId: string,
  assignmentId: string,
  googleAccessToken?: string | null
) {
  return apiFetch<{ ok: boolean }>(
    `/api/classroom/courses/${courseId}/assignments/${assignmentId}`,
    {
      method: 'DELETE',
      googleAccessToken,
    }
  );
}

/**
 * Phase B: List Google Classroom topics for a course. Returns the
 * cached server-side list (refreshed on the server before responding).
 * The server falls back to the previous cache on a transient list
 * failure — when that happens the response carries `stale: true` so
 * callers can decide whether to surface the typed error.
 */
export async function listScanCourseTopics(
  courseId: string,
  googleAccessToken?: string | null
): Promise<ClassroomTopic[]> {
  const res = await apiFetch<{ topics: ClassroomTopic[]; stale?: boolean }>(
    `/api/classroom/courses/${courseId}/topics`,
    { googleAccessToken }
  );
  return Array.isArray(res.topics) ? res.topics : [];
}

/**
 * Phase B: Create a new Google Classroom topic on a course. Returns
 * the newly created topic; the server appends it to the cached list
 * so a subsequent `listScanCourseTopics` call will include it.
 *
 * Empty / whitespace-only names are rejected at the server with
 * `TOPIC_NAME_REQUIRED` before any Google call is made.
 */
export async function createScanCourseTopic(
  courseId: string,
  name: string,
  googleAccessToken?: string | null
): Promise<ClassroomTopic> {
  return apiFetch<ClassroomTopic>(`/api/classroom/courses/${courseId}/topics`, {
    method: 'POST',
    body: JSON.stringify({ name }),
    googleAccessToken,
  });
}

/**
 * Phase C (Rubrics): GET the rubric attached to a Classroom-synced
 * assignment. The server returns `{ rubric: ClassroomRubric | null }`
 * — `null` is the canonical "Google has no rubric for this
 * assignment" value. On a server-side list failure the response can
 * carry `stale: true` together with the cached rubric (see the
 * `x-classroom-rubric-cache: stale` header) so callers may still
 * render the cached value without locking the dialog out.
 */
export async function getAssignmentRubric(
  courseId: string,
  assignmentId: string,
  googleAccessToken?: string | null
): Promise<ClassroomRubric | null> {
  const res = await apiFetch<{ rubric: ClassroomRubric | null; stale?: boolean }>(
    `/api/classroom/courses/${courseId}/assignments/${assignmentId}/rubric`,
    { googleAccessToken }
  );
  return res.rubric ?? null;
}

/**
 * Phase C (Rubrics): create the (single) rubric on a Classroom
 * assignment. Server rejects with 409 `RUBRIC_ALREADY_EXISTS` when
 * the assignment already has a rubric — callers are expected to
 * route teachers into the patch path in that case.
 */
export async function createAssignmentRubric(
  courseId: string,
  assignmentId: string,
  criteria: ClassroomRubricCriterion[],
  googleAccessToken?: string | null
): Promise<ClassroomRubric> {
  const res = await apiFetch<{ rubric: ClassroomRubric }>(
    `/api/classroom/courses/${courseId}/assignments/${assignmentId}/rubric`,
    {
      method: 'POST',
      body: JSON.stringify({ criteria }),
      googleAccessToken,
    }
  );
  return res.rubric;
}

/**
 * Phase C (Rubrics): replace the criteria on an existing rubric.
 * The server resolves the rubricId from its cache + refreshes from
 * Google once before falling back to `RUBRIC_NOT_FOUND`.
 */
export async function updateAssignmentRubric(
  courseId: string,
  assignmentId: string,
  criteria: ClassroomRubricCriterion[],
  googleAccessToken?: string | null
): Promise<ClassroomRubric> {
  const res = await apiFetch<{ rubric: ClassroomRubric }>(
    `/api/classroom/courses/${courseId}/assignments/${assignmentId}/rubric`,
    {
      method: 'PATCH',
      body: JSON.stringify({ criteria }),
      googleAccessToken,
    }
  );
  return res.rubric;
}

/**
 * Phase C (Rubrics): delete the rubric on a Classroom assignment.
 * 404 from Google is treated as "already deleted" on the server, so
 * this never throws for that case — the cache is cleared either way.
 */
export async function deleteAssignmentRubric(
  courseId: string,
  assignmentId: string,
  googleAccessToken?: string | null
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(
    `/api/classroom/courses/${courseId}/assignments/${assignmentId}/rubric`,
    {
      method: 'DELETE',
      googleAccessToken,
    }
  );
}

export async function listScanAssignments(
  courseId: string,
  googleAccessToken?: string | null,
  /**
   * When set, ask the server to skip the Classroom coursework.list
   * call and return local-only assignments. We pass this once we know
   * the course is in the coursework-denied state (so we never re-hit
   * coursework.list for it in the same session) and for explicit
   * local-only flows.
   */
  options?: { localOnly?: boolean }
) {
  const skipClassroom =
    options?.localOnly === true || hasCourseworkAccessDeniedThisSession(courseId);
  const search = skipClassroom ? '?localOnly=1' : '';
  const assignmentRequestUrl = `/api/classroom/courses/${courseId}/assignments${search}`;

  if (import.meta.env.DEV) {
    devLogGoogleApi('assignment request course id', {
      assignmentRequestCourseId: courseId,
      assignmentRequestUrl,
      isLocalCourseId: isLocalCourseId(courseId),
      skipClassroomCourseworkList: skipClassroom,
    });
  }

  return apiFetch<{ assignments: ScanAssignment[] }>(
    assignmentRequestUrl,

    { googleAccessToken }
  );
}

export async function createScanAssignment(
  courseId: string,

  body: {
    title: string;
    maxScore: number;
    description?: string;
    /**
     * Force a DoneGrading-local assignment even when the course is
     * Classroom-synced. Used by the local fallback flow so a course
     * with denied coursework access can still get a usable assignment
     * without calling Google Classroom create-coursework APIs.
     */
    localOnly?: boolean;
    /**
     * Explicit "create in Google Classroom" marker. Equivalent to
     * leaving `localOnly` unset for a synced course; surfaced as an
     * explicit flag so call sites can be self-documenting and so the
     * server can log the caller's intent.
     */
    createInClassroom?: boolean;
    /** Marker for local DoneGrading assignment mode (`donegrading`). */
    assignmentType?: 'donegrading' | 'classroom';
    /** Explicit assignment source override (`'classroom' | 'donegrading'`). */
    source?: 'classroom' | 'donegrading';
    /**
     * Optional client-supplied idempotency key. The service layer
     * generates one automatically per call when omitted; callers
     * MAY pass a stable key explicitly to survive retries (offline
     * replay, double-tap, etc.).
     */
    idempotencyKey?: string;
  },

  googleAccessToken?: string | null
) {
  const idempotencyKey =
    typeof body.idempotencyKey === 'string' && body.idempotencyKey.length > 0
      ? body.idempotencyKey
      : makeIdempotencyKey();
  const requestBody = { ...body, idempotencyKey };
  return apiFetch<{ assignment: ScanAssignment }>(
    `/api/classroom/courses/${courseId}/assignments`,

    {
      method: 'POST',

      body: JSON.stringify(requestBody),

      headers: { 'Idempotency-Key': idempotencyKey },

      googleAccessToken,
    }
  );
}

/**
 * Session-level memory of courses that already failed coursework access
 * in this tab. Mutated by the workflow when it sees
 * `CLASSROOM_COURSEWORK_ACCESS_DENIED`, read by `listScanAssignments`
 * so the second selection of the same course never re-hits
 * coursework.list for it. Resets on page reload.
 *
 * Intentionally NOT sticky across explicit recovery actions:
 *  - Sync from Classroom (capabilities may have changed server-side)
 *  - Reconnect Google (token / scope may have changed)
 *  - A successful `coursework.list` call (the previous denial is stale)
 *
 * The workflow calls `clearCourseworkAccessDenied(courseId?)` from
 * those handlers. The English 101 path never reaches this memo because
 * it never sees a 403 in the first place.
 */
const courseworkDeniedThisSession = new Set<string>();

export function markCourseworkAccessDenied(courseId: string): void {
  if (typeof courseId !== 'string' || !courseId.trim()) return;
  courseworkDeniedThisSession.add(courseId.trim());
}

export function hasCourseworkAccessDeniedThisSession(courseId: string): boolean {
  return courseworkDeniedThisSession.has(courseId.trim());
}

/**
 * Clear the in-session "coursework denied" memo. Pass a specific
 * `courseId` to clear just that course (e.g. after a successful
 * coursework.list); pass nothing to clear every entry (e.g. after
 * Sync from Classroom or Reconnect Google).
 */
export function clearCourseworkAccessDenied(courseId?: string): void {
  if (typeof courseId === 'string' && courseId.trim()) {
    courseworkDeniedThisSession.delete(courseId.trim());
    return;
  }
  courseworkDeniedThisSession.clear();
}

export function resetCourseworkAccessDeniedForTests(): void {
  courseworkDeniedThisSession.clear();
}

/**
 * Safe diagnostic probe that asks the server to classify whether a
 * synced course can list coursework today. Returns the typed summary
 * the server produced; never touches Google directly from the browser.
 *
 * Used by the workflow to distinguish "stale course id" vs "scope
 * missing" vs "true list-denied" before deciding whether to fall back
 * to local-only mode.
 */
export type ClassroomCourseworkAccessSummary = {
  courseName?: string;
  localCourseId?: string;
  storedClassroomCourseId?: string | null;
  isClassroomSynced?: boolean;
  isLocalOnly?: boolean;
  canCallGoogleClassroom?: boolean;
  requestUsesLocalId?: boolean;
  courseworkListStatus?: 'ok' | 'failed' | 'skipped' | null;
  courseworkListOk?: boolean | null;
  courseworkListCount?: number;
  courseworkListErrorCode?: string | null;
  courseworkListProviderStatus?: number | null;
  courseworkListProviderReason?: string | null;
  coursesListContainsStoredId?: boolean | null;
  coursesGetOk?: boolean | null;
  rosterListOk?: boolean | null;
  reconciledClassroomCourseId?: string | null;
  reconcileReason?: 'idMatch' | 'nameAndSection' | 'none';
  /**
   * Spec-mandated alias names. `googleApiMessage` is intentionally the
   * stable typed code (e.g. `PERMISSION_DENIED`) rather than the raw
   * provider message — provider messages can contain student emails.
   */
  googleApiStatus?: number | null;
  googleApiReason?: string | null;
  googleApiMessage?: string | null;
  finalErrorCode?:
    | 'CLASSROOM_COURSEWORK_LIST_DENIED'
    | 'CLASSROOM_COURSEWORK_CREATE_DENIED'
    | 'CLASSROOM_COURSE_ID_STALE'
    | 'CLASSROOM_COURSE_NOT_FOUND'
    | 'AUTH_RECONNECT_REQUIRED'
    | 'AUTH_PERMISSION_REQUIRED'
    | 'ASSIGNMENTS_LOAD_FAILED'
    | 'COURSE_ID_MAPPING_ERROR'
    | null;
  /** Same content as `finalErrorCode`, kept under the spec name. */
  finalClassification?: ClassroomCourseworkAccessSummary['finalErrorCode'];
  /**
   * Scope-derived booleans. `null` means "we cannot tell from the
   * stored OAuth grant" (legacy linked accounts without a persisted
   * scope list). NEVER assume `coursework.students` implies
   * `coursework.students.readonly` or vice versa.
   */
  grantedScopesKnown?: boolean;
  normalizedGrantedScopes?: string[] | null;
  grantedScopeNames?: string[] | null;
  hasClassroomCourseworkStudentsScope?: boolean | null;
  hasClassroomCourseworkStudentsReadonlyScope?: boolean | null;
  hasClassroomCourseworkMeScope?: boolean | null;
  hasClassroomCourseworkMeReadonlyScope?: boolean | null;
  hasClassroomCoursesReadonlyScope?: boolean | null;
  hasClassroomRostersReadonlyScope?: boolean | null;
  /** Best-effort role hint. Field is omitted when backend has no signal. */
  teacherRoleKnown?: boolean;
  isTeacherOrAdminForCourse?: boolean;
  capabilitiesAfterDiagnostic?: {
    classroomCourseVisible: boolean;
    rosterAvailable: boolean;
    courseworkAvailable: boolean;
    classroomPostingAvailable: boolean;
    localAssignmentsAvailable: boolean;
  } | null;
};

/**
 * @deprecated The DEV-only coursework diagnostic UI that called this
 * helper was removed. The underlying `/verify-coursework-access`
 * server route is still in place (harmless server-only endpoint with
 * its own tests), but no product UI surface invokes this helper any
 * more. Kept exported because existing service-layer tests still
 * import it.
 */
export async function verifyCourseworkAccess(
  courseId: string,
  googleAccessToken?: string | null
): Promise<ClassroomCourseworkAccessSummary> {
  return apiFetch<ClassroomCourseworkAccessSummary>(
    `/api/classroom/courses/${courseId}/verify-coursework-access`,
    { googleAccessToken }
  );
}

/**
 * Diagnostic-only summary for the COURSEWORK CREATE permission story.
 * Returned by the `verify-coursework-create-access` endpoint — the
 * server NEVER calls `coursework.create` for this; every probe is
 * read-only. Tokens, student emails, teacher names/emails, and roster
 * contents are NEVER included by the server.
 *
 * `null` on a boolean field means "we cannot tell from the available
 * signals" (e.g. probe was not callable, or stored OAuth grant has no
 * scope list). Callers MUST surface the unknown state rather than
 * green-lighting create on that signal alone — the spec is explicit
 * that we do not silently fall back to local when the teacher clicked
 * "Create assignment".
 */
export type ClassroomCourseworkCreateAccessSummary = {
  courseName?: string;
  localCourseId?: string;
  classroomCourseId?: string | null;
  isClassroomSynced?: boolean;
  isLocalOnly?: boolean;
  canCallGoogleClassroom?: boolean;
  requestUsesLocalId?: boolean;
  // Scope-derived fields (mirrored from the existing diagnostic so the
  // dev panel can reuse the same renderer).
  grantedScopesKnown?: boolean;
  normalizedGrantedScopes?: string[] | null;
  grantedScopeNames?: string[] | null;
  hasClassroomCourseworkStudentsScope?: boolean | null;
  hasClassroomCourseworkStudentsReadonlyScope?: boolean | null;
  hasClassroomCourseworkMeScope?: boolean | null;
  hasClassroomCourseworkMeReadonlyScope?: boolean | null;
  hasClassroomCoursesReadonlyScope?: boolean | null;
  hasClassroomRostersReadonlyScope?: boolean | null;
  /** True when the stored OAuth grant carries a usable scope list. */
  courseworkCreateScopeKnown?: boolean;
  /** Short-id form of any required write scopes the stored grant lacks. */
  missingCourseworkWriteScopes?: string[] | null;
  // Probe results.
  coursesGetOk?: boolean | null;
  rosterListOk?: boolean | null;
  teacherListOk?: boolean | null;
  isTeacherForCourse?: boolean | null;
  teacherRoleKnown?: boolean;
  courseworkListOk?: boolean | null;
  courseworkListCount?: number;
  // Derived gates.
  canListCoursework?: boolean | null;
  canCreateCoursework?: boolean | null;
  // Spec aliases.
  googleApiStatus?: number | null;
  googleApiReason?: string | null;
  googleApiMessage?: string | null;
  finalErrorCode?:
    | 'AUTH_RECONNECT_REQUIRED'
    | 'AUTH_PERMISSION_REQUIRED'
    | 'CLASSROOM_COURSEWORK_CREATE_DENIED'
    | 'CLASSROOM_COURSE_NOT_FOUND'
    | 'CLASSROOM_COURSE_NOT_SYNCED'
    | 'COURSE_ID_MAPPING_ERROR'
    | null;
  finalClassification?:
    | 'AUTH_RECONNECT_REQUIRED'
    | 'CLASSROOM_COURSEWORK_CREATE_DENIED'
    | 'CLASSROOM_COURSEWORK_CREATE_OK'
    | 'CLASSROOM_COURSEWORK_CREATE_SCOPE_MISSING'
    | 'CLASSROOM_COURSEWORK_CREATE_SCOPE_OK'
    | 'CLASSROOM_COURSEWORK_CREATE_SCOPE_UNKNOWN'
    | 'CLASSROOM_COURSE_NOT_FOUND'
    | 'CLASSROOM_COURSE_NOT_SYNCED'
    | 'COURSE_ID_MAPPING_ERROR'
    | null;
};

/**
 * @deprecated The DEV-only coursework CREATE diagnostic UI that
 * called this helper was removed. The underlying
 * `/verify-coursework-create-access` server route is still in place
 * (harmless server-only endpoint with its own tests), but no product
 * UI surface invokes this helper any more. Kept exported because
 * existing service-layer tests still import it.
 */
export async function verifyCourseworkCreateAccess(
  courseId: string,
  googleAccessToken?: string | null
): Promise<ClassroomCourseworkCreateAccessSummary> {
  return apiFetch<ClassroomCourseworkCreateAccessSummary>(
    `/api/classroom/courses/${courseId}/verify-coursework-create-access`,
    { googleAccessToken }
  );
}

export async function registerClassroomAssignment(
  courseId: string,

  body: { classroomCourseWorkId: string },

  googleAccessToken?: string | null
) {
  return apiFetch<{ assignment: ScanAssignment }>(
    `/api/classroom/courses/${courseId}/assignments/register`,

    {
      method: 'POST',

      body: JSON.stringify(body),

      googleAccessToken,
    }
  );
}

export async function fetchRoster(courseId: string, googleAccessToken?: string | null) {
  const rosterRequestUrl = `/api/classroom/courses/${courseId}/roster`;

  if (import.meta.env.DEV) {
    devLogGoogleApi('roster request course id', {
      rosterRequestCourseId: courseId,
      rosterRequestUrl,
      isLocalCourseId: isLocalCourseId(courseId),
    });
  }

  return apiFetch<{ students: ScanRosterStudent[] }>(
    rosterRequestUrl,

    { googleAccessToken }
  );
}

export async function createRubric(
  body: {
    assignmentId: string;

    inputMethod: string;

    rawText: string;

    criteria?: unknown[];

    /**
     * Optional free-form "quick rubric" note the teacher can add. Aggregate
     * grading guidance only (strictness, ESL context, …) — never student
     * data. Folded into the AI grading prompt server-side.
     */
    instructions?: string;
  },

  googleAccessToken?: string | null
) {
  return apiFetch<{ rubric: ScanRubric }>('/api/rubrics', {
    method: 'POST',

    body: JSON.stringify(body),

    googleAccessToken,
  });
}

export async function createRubricFromImage(
  assignmentId: string,

  base64Image: string,

  googleAccessToken?: string | null,

  instructions?: string
) {
  return apiFetch<{ rubric: ScanRubric }>('/api/rubrics/from-image', {
    method: 'POST',

    body: JSON.stringify({ assignmentId, base64Image, instructions }),

    googleAccessToken,
  });
}

/**
 * Snapshot of an in-progress scan session: every page captured so far plus
 * every submission they were attached to. Drives the post-scan review
 * screen, which has to display thumbnails + per-page match status before
 * grading runs.
 */
export async function getScanSessionSnapshot(sessionId: string, googleAccessToken?: string | null) {
  return apiFetch<{
    session: ScanSession;
    submissions: (ScanSubmission & { displayNameGuess?: string })[];
    pages: (ScanPage & {
      imageBase64?: string;
      capturedAt?: string;
      backgroundLeakScore?: number | null;
      /** Page lifecycle status. Absent on legacy pages → treat as active. */
      status?: 'active' | 'ignored' | null;
      // Coarse crop signals (non-PII) the teacher-facing review UI uses to
      // decide whether a page needs crop attention and which bytes are safe
      // to show. Persisted server-side at upload time.
      cropMode?: 'perspective' | 'bbox' | 'fullFrameFallback' | null;
      cropQuality?: 'clean_document' | 'needs_review' | 'fallback_full_frame' | 'failed' | null;
      displaySource?:
        | 'reused_clean_document'
        | 'tighter_pass_clean_document'
        | 'teacher_accepted'
        | 'none'
        | null;
      displayImageBase64?: string | null;
      teacherAcceptedCrop?: boolean | null;
      cropSource?: 'auto' | 'manual' | null;
      cropEditedManually?: boolean | null;
      cropUpdatedAt?: string | null;
    })[];
    /** Prior export job when session was already published (refresh recovery). */
    lastExportJob?: {
      id: string;
      status?: string;
      results?: ExportResults | null;
      createdAt?: string | null;
    } | null;
  }>(`/api/scan/sessions/${sessionId}`, { googleAccessToken });
}

/**
 * Save a teacher-edited (manual) crop for a page. The client warps the
 * teacher-chosen quad and sends the resulting paper-only JPEG bytes; the
 * server preserves the original, replaces the display/OCR bytes, and marks
 * the crop as a teacher-validated clean document (clearing needs-attention).
 */
export async function saveManualPageCrop(
  sessionId: string,
  pageId: string,
  body: {
    imageBase64: string;
    displayImageBase64?: string;
    cropMode?: 'perspective' | 'bbox' | 'fullFrameFallback';
    cropQuality?: 'clean_document' | 'needs_review' | 'fallback_full_frame' | 'failed';
    backgroundLeakScore?: number;
  },
  googleAccessToken?: string | null
) {
  return apiFetch<{
    ok: boolean;
    sessionId: string;
    pageId: string;
    page: {
      id: string;
      cropMode: string;
      cropQuality: string;
      cropSource: 'manual';
      cropEditedManually: true;
      cropUpdatedAt: string;
      teacherAcceptedCrop: true;
      displaySource: 'teacher_accepted';
    };
  }>(`/api/scan/sessions/${sessionId}/pages/${pageId}/crop`, {
    method: 'PUT',
    body: JSON.stringify(body),
    googleAccessToken,
  });
}

export async function createScanSession(
  body: { courseId: string; assignmentId: string; rubricId?: string },

  googleAccessToken?: string | null
) {
  return apiFetch<{ session: ScanSession }>('/api/scan/sessions', {
    method: 'POST',

    body: JSON.stringify(body),

    googleAccessToken,
  });
}

/**
 * Optional crop-quality metadata attached to a scan-page upload. Drives
 * the server-side publish gate that refuses to ship background-heavy
 * artifacts to students / Drive unless the teacher explicitly accepted
 * the crop. See `CropArtifactQuality` in `lib/scanGrading/perspectiveCrop.ts`
 * for the value semantics — kept as a duck-typed string here so this
 * service module does not depend on the canvas-only crop module.
 */
export type UploadCropMetadata = {
  cropMode?: 'perspective' | 'bbox' | 'fullFrameFallback' | null;
  cropQuality?: 'clean_document' | 'needs_review' | 'fallback_full_frame' | 'failed' | null;
  backgroundLeakScore?: number | null;
};

/**
 * Phase 1 display-artifact upload metadata. `displayBase64Image` is the
 * student/teacher-facing scan bytes (paper-only, validated). If the
 * client could not derive a shippable display artifact (e.g. background
 * leak too high) it sends `displayBase64Image: null` and the server
 * downgrades stored `cropQuality` to `needs_review` so the export gate
 * blocks until teacher accept. See `lib/scanGrading/displayCrop.ts`.
 */
export type UploadDisplayMetadata = {
  displayBase64Image?: string | null;
  displaySource?:
    | 'reused_clean_document'
    | 'tighter_pass_clean_document'
    | 'teacher_accepted'
    | 'none'
    | null;
};

/**
 * Upload a captured page to a scan session. Server response is the canonical
 * `{ ok, pageId, pageNumber, ocrStatus, detectedName, matchStatus,
 *   matchedStudent, candidates, needsReview }` shape; legacy `page`, `submission`,
 * `match` fields are kept for back-compat with older callers.
 */
export async function uploadScanPage(
  sessionId: string,

  base64Image: string,

  clientRequestId?: string,

  googleAccessToken?: string | null,
  cropMetadata?: UploadCropMetadata | null,
  displayMetadata?: UploadDisplayMetadata | null
) {
  return apiFetch<{
    ok?: boolean;

    /** Echo of the scan session this page was written to. */
    sessionId?: string;

    page: { id: string };

    /** Convenience alias for `page.id`. */
    pageId?: string;

    /** 1-based page index in this session, after this upload. */
    pageNumber?: number;

    submission: { id: string; studentId?: string };

    match: {
      candidates: { studentId: string; name: string; score: number; reason?: ScanMatchReason }[];
      needsConfirmation: boolean;
      /** Medium-band margin auto-match — `matchStatus` is `matched` but below
       * the high-confidence threshold; the camera shows a "verify if needed"
       * hint. See `matchNameToRoster`. */
      verify?: boolean;
      reason?: ScanMatchReason;
    };

    /** Top-level mirror of `match.verify` (medium-band margin auto-match). */
    verify?: boolean;

    /** Top-level candidates alias for the canonical response shape. */
    candidates?: { studentId: string; name: string; score: number; reason?: ScanMatchReason }[];

    duplicate?: boolean;

    ocrStatus?: 'success' | 'failed' | 'skipped';

    detectedName?: string | null;

    matchStatus?: 'matched' | 'possible_match' | 'no_roster_match' | 'name_not_found';

    /** Stable reason code from server roster matcher. */
    matchReason?: ScanMatchReason;

    matchedStudent?: { id: string; name: string } | null;

    /** Roster student id of the active student before this capture. */
    currentStudentId?: string | null;

    needsReview?: boolean;
    cropQuality?: 'clean_document' | 'needs_review' | 'fallback_full_frame' | 'failed';
    cropMode?: 'perspective' | 'bbox' | 'fullFrameFallback';
    backgroundLeakScore?: number;
    /**
     * Echoed display-artifact provenance. May differ from the
     * client-supplied value when server defense-in-depth downgraded
     * a clean_document with `displaySource === 'none'`.
     */
    displaySource?:
      | 'reused_clean_document'
      | 'tighter_pass_clean_document'
      | 'teacher_accepted'
      | 'none';
  }>(`/api/scan/sessions/${sessionId}/pages`, {
    method: 'POST',

    body: JSON.stringify({
      base64Image,
      clientRequestId,
      // Forward crop metadata so the server can store quality on the
      // page record and the publish gate can refuse to ship background-
      // heavy artifacts. Sent as top-level keys for back-compat with the
      // existing endpoint shape (server ignores unknown fields).
      ...(cropMetadata?.cropMode ? { cropMode: cropMetadata.cropMode } : {}),
      ...(cropMetadata?.cropQuality ? { cropQuality: cropMetadata.cropQuality } : {}),
      ...(typeof cropMetadata?.backgroundLeakScore === 'number'
        ? { backgroundLeakScore: cropMetadata.backgroundLeakScore }
        : {}),
      // Phase 1: separate display artifact bytes. Sent as a top-level
      // key so the server can persist `page.displayImageBase64`
      // independent of `page.imageBase64` (which stays OCR-safe).
      ...(typeof displayMetadata?.displayBase64Image === 'string' &&
      displayMetadata.displayBase64Image.length > 0
        ? { displayBase64Image: displayMetadata.displayBase64Image }
        : {}),
      ...(displayMetadata?.displaySource ? { displaySource: displayMetadata.displaySource } : {}),
    }),

    googleAccessToken,
  });
}

/**
 * Progressive Batch Capture — upload a JPEG/PNG blob without base64 JSON.
 * Falls back to `uploadScanPage` when the binary route is unavailable.
 */
export async function uploadScanPageBinary(
  sessionId: string,
  imageBlob: Blob,
  clientRequestId?: string,
  googleAccessToken?: string | null,
  cropMetadata?: UploadCropMetadata | null
) {
  const params = new URLSearchParams();
  if (clientRequestId) params.set('clientRequestId', clientRequestId);
  if (cropMetadata?.cropMode) params.set('cropMode', cropMetadata.cropMode);
  if (cropMetadata?.cropQuality) params.set('cropQuality', cropMetadata.cropQuality);
  if (typeof cropMetadata?.backgroundLeakScore === 'number') {
    params.set('backgroundLeakScore', String(cropMetadata.backgroundLeakScore));
  }
  if (cropMetadata && 'displaySource' in (cropMetadata as object)) {
    /* displaySource sent via header below when present on extended meta */
  }
  const qs = params.toString();
  const path = `/api/scan/sessions/${sessionId}/pages/binary${qs ? `?${qs}` : ''}`;

  return apiFetch<{
    ok?: boolean;
    sessionId?: string;
    page: { id: string };
    pageId?: string;
    pageNumber?: number;
    submission: { id: string; studentId?: string };
    match: {
      candidates: { studentId: string; name: string; score: number; reason?: ScanMatchReason }[];
      needsConfirmation: boolean;
      verify?: boolean;
      reason?: ScanMatchReason;
    };
    verify?: boolean;
    candidates?: { studentId: string; name: string; score: number; reason?: ScanMatchReason }[];
    duplicate?: boolean;
    ocrStatus?: 'success' | 'failed' | 'skipped';
    detectedName?: string | null;
    matchStatus?: 'matched' | 'possible_match' | 'no_roster_match' | 'name_not_found';
    matchReason?: ScanMatchReason;
    matchedStudent?: { id: string; name: string } | null;
    currentStudentId?: string | null;
    needsReview?: boolean;
    cropQuality?: 'clean_document' | 'needs_review' | 'fallback_full_frame' | 'failed';
    cropMode?: 'perspective' | 'bbox' | 'fullFrameFallback';
    backgroundLeakScore?: number;
    displaySource?:
      | 'reused_clean_document'
      | 'tighter_pass_clean_document'
      | 'teacher_accepted'
      | 'none';
  }>(path, {
    method: 'POST',
    body: imageBlob,
    headers: {
      'Content-Type': imageBlob.type || 'image/jpeg',
      ...(clientRequestId ? { 'x-client-request-id': clientRequestId } : {}),
    },
    googleAccessToken,
  });
}

/**
 * Phase 1 server-side gate response when one or more pages need
 * teacher review before the entire Return-work action is allowed to
 * fire any side effects. Thrown as a `ScanApiError` with `status=409`
 * and `body.code='CROP_REVIEW_REQUIRED'`. The client reads
 * `body.blockedPageCount` + `body.blockedPages` from
 * `ScanApiError.body` to render the dedicated 'Review scans' banner +
 * modal (no SENT chips, no generic error toast).
 */
export type CropReviewRequiredBody = {
  code: 'CROP_REVIEW_REQUIRED';
  blockedPageCount: number;
  blockedPages: {
    pageId: string;
    submissionId?: string | null;
    cropMode?: 'perspective' | 'bbox' | 'fullFrameFallback' | null;
    cropQuality?: 'clean_document' | 'needs_review' | 'fallback_full_frame' | 'failed' | null;
    backgroundLeakScore?: number | null;
    /** Plain-English reason rendered inside the BlockedScansModal row. */
    reason?: string | null;
  }[];
};

export function isCropReviewRequiredError(
  error: unknown
): error is ScanApiError & { body: CropReviewRequiredBody } {
  if (!(error instanceof ScanApiError)) return false;
  if (error.status !== 409) return false;
  const body = error.body as Partial<CropReviewRequiredBody>;
  return body?.code === 'CROP_REVIEW_REQUIRED' && Array.isArray(body?.blockedPages);
}

/**
 * Mark a scan page as "teacher accepted" so publish will include it
 * even when its `cropQuality` is below `clean_document`. Server-side
 * gate enforced by the publish path; the camera UI fires this when the
 * teacher taps "Accept crop" on the result card.
 */
export async function acceptScanPageCrop(pageId: string, googleAccessToken?: string | null) {
  return apiFetch<{
    ok: boolean;
    page: { id: string; teacherAcceptedCrop: boolean };
  }>(`/api/scan/pages/${pageId}/accept-crop`, {
    method: 'POST',
    googleAccessToken,
  });
}

export async function confirmSubmissionMatch(
  submissionId: string,

  studentId: string,

  googleAccessToken?: string | null,
  /**
   * Pass `false` to opt out of merging when an existing submission for
   * `studentId` already exists in the same session. Defaults to merging
   * (single card per student).
   */
  mergeIntoExisting?: boolean
) {
  return apiFetch<{ submission: unknown; merged?: boolean }>(
    `/api/scan/submissions/${submissionId}/match`,
    {
      method: 'PUT',

      body: JSON.stringify({ studentId, mergeIntoExisting }),

      googleAccessToken,
    }
  );
}

/**
 * Delete a single captured page from a scan session (Preview screen).
 *
 * Backed by `DELETE /api/scan/sessions/:sessionId/pages/:pageId`. The
 * server removes the page from its submission's `pageIds` and drops the
 * submission entirely when it is left empty and non-finalized. It NEVER
 * touches other students' pages/submissions.
 *
 * Structured error codes surfaced on `ScanApiError.body.code`:
 *  - `PAGE_NOT_FOUND` (404)      — page missing or not in this session.
 *  - `FORBIDDEN` (403)           — session not owned by the caller.
 *  - `SUBMISSION_FINALIZED` (409)— page belongs to a finalized grade;
 *    the caller must unwind the grade before deleting (no silent loss).
 *
 * Returns refreshed `counts` so the caller can reconcile without a
 * second round-trip; callers normally re-fetch the snapshot anyway.
 */
export async function deleteScanPage(
  sessionId: string,
  pageId: string,
  googleAccessToken?: string | null
) {
  return apiFetch<{
    ok: boolean;
    sessionId: string;
    removedPageId: string;
    /** True when the page was already gone — the delete is idempotent. */
    alreadyDeleted?: boolean;
    code?: 'PAGE_ALREADY_DELETED';
    submissionRemoved: boolean;
    counts: { pageCount: number; submissionCount: number };
  }>(`/api/scan/sessions/${sessionId}/pages/${pageId}`, {
    method: 'DELETE',
    googleAccessToken,
  });
}

/**
 * Ignore (or restore) a scanned page. Ignoring is a reversible tombstone —
 * the page is excluded from active matching/review/grading/export but its
 * bytes are retained so the teacher can restore it before grading. Pass
 * `ignored: false` to restore. The endpoint is idempotent.
 */
export async function setScanPageIgnored(
  sessionId: string,
  pageId: string,
  ignored: boolean,
  googleAccessToken?: string | null
) {
  return apiFetch<{
    ok: boolean;
    sessionId: string;
    pageId: string;
    status: 'active' | 'ignored';
    counts: { activePageCount: number; ignoredPageCount: number };
  }>(`/api/scan/sessions/${sessionId}/pages/${pageId}/ignore`, {
    method: 'PUT',
    body: JSON.stringify({ ignored }),
    googleAccessToken,
  });
}

export async function completeCapture(sessionId: string, googleAccessToken?: string | null) {
  return apiFetch<{ ok: boolean }>(`/api/scan/sessions/${sessionId}/complete-capture`, {
    method: 'POST',

    googleAccessToken,
  });
}

export async function runSessionGrading(
  sessionId: string,
  googleAccessToken?: string | null,
  options?: { retryFailedOnly?: boolean; submissionId?: string; forceRegrade?: boolean }
) {
  return apiFetch<{ ok: boolean; session: ScanSession; progress?: GradeProgress }>(
    `/api/scan/sessions/${sessionId}/grade`,

    {
      method: 'POST',
      body: JSON.stringify({
        retryFailedOnly: options?.retryFailedOnly === true,
        forceRegrade: options?.forceRegrade === true,
        ...(options?.submissionId ? { submissionId: options.submissionId } : {}),
      }),
      googleAccessToken,
    }
  );
}

export async function getSessionReview(sessionId: string, googleAccessToken?: string | null) {
  return apiFetch<{
    items: ReviewItem[];

    missing: ScanRosterStudent[];

    session: ScanSession;
  }>(`/api/scan/sessions/${sessionId}/review`, { googleAccessToken });
}

export async function patchSubmissionReview(
  submissionId: string,

  body: { score?: number; feedback?: string },

  googleAccessToken?: string | null
) {
  return apiFetch<{ grading: unknown; feedback: unknown }>(
    `/api/scan/submissions/${submissionId}/review`,

    { method: 'PATCH', body: JSON.stringify(body), googleAccessToken }
  );
}

export async function approveSubmission(submissionId: string, googleAccessToken?: string | null) {
  return apiFetch<{ finalizedGrade: unknown }>(`/api/scan/submissions/${submissionId}/approve`, {
    method: 'POST',

    googleAccessToken,
  });
}

export type RegenerateFeedbackResult = {
  submissionId: string;
  feedbackDraftId: string;
  feedback: string;
  teacherEdited: false;
  status: string;
};

export type RegenerateFeedbackOptions = {
  feedbackTone?: FeedbackTone;
  confirmOverwriteTeacherEdits?: boolean;
};

/**
 * Regenerate AI feedback for one submission (Feedback 7D).
 * Sends only normalized tone and optional overwrite confirmation — no feedback/OCR text.
 */
export async function regenerateSubmissionFeedback(
  sessionId: string,
  submissionId: string,
  options: RegenerateFeedbackOptions,
  googleAccessToken?: string | null
) {
  const feedbackTone = normalizeFeedbackTone(options.feedbackTone);
  const body: Record<string, unknown> = { feedbackTone };
  if (options.confirmOverwriteTeacherEdits === true) {
    body.confirmOverwriteTeacherEdits = true;
  }
  return apiFetch<RegenerateFeedbackResult>(
    `/api/scan/sessions/${sessionId}/submissions/${submissionId}/regenerate-feedback`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      googleAccessToken,
    }
  );
}

export function isTeacherEditConfirmationRequired(
  error: unknown
): error is ScanApiError & { body: { code: 'TEACHER_EDIT_CONFIRMATION_REQUIRED' } } {
  if (!(error instanceof ScanApiError)) return false;
  if (error.status !== 409) return false;
  return error.body?.code === 'TEACHER_EDIT_CONFIRMATION_REQUIRED';
}

export async function setMissingStudentStatus(
  sessionId: string,

  studentId: string,

  status: string,

  googleAccessToken?: string | null
) {
  return apiFetch<{ ok: boolean }>(`/api/scan/sessions/${sessionId}/missing/${studentId}`, {
    method: 'PUT',

    body: JSON.stringify({ status }),

    googleAccessToken,
  });
}

export async function exportSession(
  sessionId: string,

  flags: {
    post: boolean;
    email: boolean;
    drive: boolean;
    pdf: boolean;
    retryJobId?: string;
    reviewConfirmed?: boolean;
  },

  googleAccessToken?: string | null
): Promise<ExportSessionResponse> {
  const response = await apiFetch<ExportSessionResponse>(
    `/api/scan/sessions/${sessionId}/export`,

    {
      method: 'POST',

      body: JSON.stringify(flags),

      googleAccessToken,
    }
  );
  return response;
}

/**
 * Teacher-initiated delivery of a Teaching Insights / "Next Teaching Moves"
 * artifact. The client sends STRUCTURED data only (never raw HTML); the
 * server validates ownership, derives the trusted class/assignment names,
 * renders the artifact, and saves to Drive / emails the teacher. Everything
 * here is fired only on an explicit teacher click.
 */
export type InsightArtifactType =
  | 'tomorrows_plan'
  | 'small_group_cards'
  | 'student_check_in_list'
  | 'family_drafts'
  | 'intervention_notes'
  | 'teaching_evidence_summary'
  | 'worksheet'
  | 'exit_ticket'
  | 'practice_activity'
  | 'printable_support'
  | 'enrichment_task'
  | 'resource_bundle'
  | 'custom_resource';

export interface InsightArtifactSection {
  heading: string;
  lines: string[];
}

export interface InsightArtifactPayload {
  sessionId: string;
  artifactType: InsightArtifactType;
  /** Stable id for this artifact instance — used for idempotency dedupe. */
  artifactId: string;
  title?: string;
  subtitle?: string;
  sections: InsightArtifactSection[];
  /** Set when the teacher edited the content before delivering. */
  teacherEdited?: boolean;
  /** Optional single-blob edited content (overrides `sections` server-side). */
  teacherEditedContent?: string | null;
}

export interface InsightDriveResult {
  ok: true;
  status: 'saved';
  fileId: string;
  webViewLink: string;
  /** Which readable format Drive stored — a native Google Doc, or a text fallback. */
  format?: 'gdoc' | 'txt';
}

export interface InsightEmailResult {
  ok: true;
  status: 'sent';
  recipient: string;
}

/** Save a single Teaching Insights artifact to the teacher's Google Drive. */
export async function saveInsightArtifactToDrive(
  payload: InsightArtifactPayload,
  googleAccessToken?: string | null
): Promise<InsightDriveResult> {
  return apiFetch<InsightDriveResult>('/api/insights/artifacts/drive', {
    method: 'POST',
    body: JSON.stringify(payload),
    googleAccessToken,
  });
}

/** Email a single Teaching Insights artifact to the authenticated teacher only. */
export async function emailInsightArtifactToSelf(
  payload: InsightArtifactPayload,
  googleAccessToken?: string | null
): Promise<InsightEmailResult> {
  return apiFetch<InsightEmailResult>('/api/insights/artifacts/email-self', {
    method: 'POST',
    body: JSON.stringify(payload),
    googleAccessToken,
  });
}

export async function replayOfflineBatch(
  items: unknown[],

  googleAccessToken?: string | null
) {
  return apiFetch<{ outcomes: unknown[] }>('/api/scan/offline/batch', {
    method: 'POST',

    body: JSON.stringify({ items }),

    googleAccessToken,
  });
}

/** Per-collection counts of what a server-side deletion removed. */
export type GovernanceRemovedCounts = Record<string, number>;

/**
 * Purge ALL of the authenticated user's server-side scan-grading data
 * (courses, assignments, rubrics, sessions, pages, submissions, grades,
 * feedback, jobs, Google account row, user row). Backed by
 * `DELETE /api/governance/account`, which cascades server-side and is
 * scoped to the caller's uid.
 *
 * Called by the account-deletion flow BEFORE any irreversible Firebase
 * account deletion or local-state clearing, so that a teacher's stored
 * data is provably removed from the backend first. Uses the Firebase ID
 * token bearer (non-Google route) via `apiFetch`.
 */
export async function purgeAccountServerData(): Promise<{
  ok: boolean;
  removed?: GovernanceRemovedCounts;
}> {
  return apiFetch<{ ok: boolean; removed?: GovernanceRemovedCounts }>('/api/governance/account', {
    method: 'DELETE',
  });
}
